import { postJson } from "./http";

/**
 * What we already read out of a client's Zoho Books — the datasets the
 * review screens (GST, ledgers, TDS/TCS, banks, currencies) render, straight
 * from our `onboarding_zoho_*` tables. Pure reads plus one deliberate
 * exception: `refreshZohoDataset`, which re-pulls a single dataset from Zoho
 * on demand.
 */

/**
 * A TINYINT column straight off a table row.
 *
 * MySQLi hands every column back as a string, so a false flag arrives as the
 * string "0" — which is truthy in JavaScript. Never test one of these directly;
 * run it through `dbTrue`.
 */
export type DbBool = string | number | boolean | null;

/** Reads a MySQL flag the way MySQL means it, "0" included. */
export const dbTrue = (value: DbBool): boolean =>
  value === true || value === 1 || value === "1";

/**
 * The organisation profile as Zoho reports it, not as we were told it.
 *
 * The row from `onboarding_zoho_organisations`, column names and all.
 */
export interface ZohoOrganisationDTO {
  zoho_organization_id: string;
  name: string | null;
  user_role: string | null;
  gstin: string | null;
  is_gst_registered: DbBool;
  is_composition_scheme: DbBool;
  is_sez: DbBool;
  tax_basis: string | null;
  fiscal_year_start: string | null;
  base_currency: string | null;
  time_zone: string | null;
  date_format: string | null;
  industry: string | null;
  contact_name: string | null;
  email: string | null;
  portal_name: string | null;
  edition: string | null;
  account_created_on: string | null;
  city: string | null;
  state: string | null;
  state_code: string | null;
  country: string | null;
}

/**
 * One general ledger account, as `onboarding_zoho_ledgers` stores it.
 *
 * A tree, not a list: `parent_account_id` is Zoho's own id of the parent, and
 * `depth` is 0 at the top. `has_children` was worked out when the row was read,
 * so a screen can draw the hierarchy in one pass. Every flag is a `DbBool`.
 */
export interface ZohoLedgerDTO {
  account_id: string;
  account_name: string | null;
  account_code: string | null;
  /** Zoho's own type, e.g. "other_current_asset" — 17 live values, more exist. */
  account_type: string | null;
  parent_account_id: string | null;
  parent_account_name: string | null;
  depth: number | string;
  has_children: DbBool;
  is_active: DbBool;
  is_system: DbBool;
  is_user_created: DbBool;
  description: string | null;
  /** Set when a reviewer ticked this row off. Null until then. */
  confirmed_at: string | null;
}

/**
 * One GST registration, as `onboarding_zoho_gstins` stores it.
 *
 * A row per Zoho branch, which is how a multi-GSTIN organisation is
 * enumerated. The e-invoicing columns come from a second read and are patched
 * onto the registration they name, so `is_einvoice_enabled` is null when that
 * read has not run or named a GSTIN this list does not have — which is the
 * common case, not the rare one: Zoho answers e-invoicing once per
 * organisation, and an organisation with Locations on gets a payload naming no
 * registration at all.
 *
 * Null still means "we did not get an answer against this row". Both screens
 * that read this column now display it as OFF anyway, because the reads that
 * leave it null are overwhelmingly ones where Zoho said off, and because the
 * flag drives nothing beyond those two screens. Do not read null as a distinct
 * third state here; if this column ever starts driving tenant behaviour, that
 * default has to be revisited first.
 */
export interface ZohoGstinDTO {
  branch_id: string;
  gstin: string | null;
  branch_name: string | null;
  is_primary: DbBool;
  is_active: DbBool;
  status: string | null;
  is_einvoice_enabled: DbBool;
  einvoice_username: string | null;
  einvoice_portal_name: string | null;
  einvoice_lock_date: string | null;
  tax_settings_id: string | null;
  city: string | null;
  state: string | null;
  state_code: string | null;
  country: string | null;
  address: string | null;
  confirmed_at: string | null;
}

/**
 * One tax rate, as `onboarding_zoho_taxes` stores it. GST, TDS and TCS share
 * the table and the shape; `category` says which.
 *
 * `status` is Zoho's own word and it is date-driven: a rate reads `Expired`
 * once today is past its `end_date`, which does not mean unusable. Zoho offers
 * a rate on a transaction whose date falls inside the rate's own period, so
 * expired rates are what back-dated entries need.
 *
 * `tds_payable_account_id` is the liability side, filled by a second per-rate
 * read. Null on TCS rows, which carry one account that is already the payable
 * one, and null on any TDS rate whose detail read failed.
 */
export interface ZohoTaxDTO {
  tax_id: string;
  category: string;
  tax_name: string | null;
  tax_percentage: string | number | null;
  tax_type: string | null;
  /** Zoho's section key: "194H", or "206C_1H_6CR", or an Income Tax Act 2025 name. */
  tax_specific_type: string | null;
  output_tax_account_name: string | null;
  /**
   * WHICH SIDE THIS IS DEPENDS ON THE TAX. On a TDS rate it is the receivable;
   * on a TCS rate it is the payable. Zoho's naming, not ours — the pairing is
   * spelled out in `TDS_LEDGER_SLOTS` and `TCS_LEDGER_SLOTS`.
   */
  tax_account_id: string | null;
  /** TDS only. Null on a TCS rate, whose payable is `tax_account_id`. */
  tds_payable_account_id: string | null;
  /** TCS only. Null on a TDS rate, whose receivable is `tax_account_id`. */
  tcs_receivable_account_id: string | null;
  tax_specification: string | null;
  status: string | null;
  /**
   * Switched off in Zoho, which is a different thing from expired: an inactive
   * rate is in date and still cannot be used. Zoho's own list filters them
   * separately.
   */
  is_inactive: DbBool;
  start_date: string | null;
  end_date: string | null;
  confirmed_at: string | null;
}

/**
 * One banking account, as `onboarding_zoho_bank_accounts` stores it.
 *
 * "Bank" is Zoho's word for the whole Banking screen, so cash and credit-card
 * accounts arrive in the same list — `account_type` separates them.
 *
 * `masked_number` is Zoho's own mask ("xxxx5432"), and it is the only form of
 * the number that exists here: the full number is never read and the sibling
 * `bank_account_number` field, which is a hash, is deliberately not stored.
 * Null on cash accounts and on any account added without one.
 *
 * `uncategorised_count` is how many fetched transactions are sitting
 * uncategorised in Zoho — the one number on this screen that is a to-do rather
 * than a fact.
 */
export interface ZohoBankAccountDTO {
  account_id: string;
  account_name: string | null;
  /** "bank", "credit_card" or "cash". */
  account_type: string | null;
  account_sub_type: string | null;
  masked_number: string | null;
  /** Zoho carries the IFSC in `routing_number`. Null unless the client filled it in. */
  ifsc: string | null;
  bank_name: string | null;
  currency_code: string | null;
  balance: string | number | null;
  is_primary: DbBool;
  is_active: DbBool;
  uncategorised_count: number | string;
  confirmed_at: string | null;
  /**
   * ZOUR-120: set by the CA, not read from Zoho (Zoho has no such flag), and
   * kept across re-reads. Bank-type accounts only. Go-live carries it to the
   * tenant's `bank_accounts.is_petty_cash`. Absent before the master migration.
   */
  is_petty_cash?: DbBool;
}

/**
 * One currency enabled on the organisation, as `onboarding_zoho_currencies`
 * stores it. Zoho lists only the enabled ones; there is no endpoint for the
 * full catalog of currencies it supports.
 *
 * `exchange_rate` comes back as 0 from the list endpoint for every row — the
 * real rates live under `/settings/currencies/{id}/exchangerates`, which we do
 * not read. Treat a 0 as "not read", never as a rate.
 */
export interface ZohoCurrencyDTO {
  currency_id: string;
  currency_code: string | null;
  currency_name: string | null;
  currency_symbol: string | null;
  /** Zoho's own sample, e.g. "12,34,567.89" for INR — the grouping differs per currency. */
  currency_format: string | null;
  price_precision: number | string;
  is_base_currency: DbBool;
  exchange_rate: string | number | null;
  effective_date: string | null;
  auto_exchange_rate_enabled: DbBool;
  confirmed_at: string | null;
}

/** The datasets a review screen can ask for by name. */
export type ZohoDatasetKey =
  | "organisation"
  | "gstins"
  | "ledgers"
  | "tds_rates"
  | "tcs_rates"
  | "tax_rates"
  | "bank_accounts"
  | "currencies";

/**
 * Our record of having read one dataset — not the dataset itself.
 *
 * This is what lets a screen say how old the rows in front of it are, and it
 * is why an empty list is readable: `ok` with no rows means the organisation
 * genuinely has none, `failed` means we never managed to ask.
 *
 * `fetched_at` is UTC in MySQL's "YYYY-MM-DD HH:MM:SS" form, with no zone on
 * it — `new Date(value)` would read it as local time and be hours out. Null
 * until a read has actually succeeded: a failed re-read never restamps it, so
 * it always names the last good copy rather than the last attempt.
 */
export interface ZohoDatasetReadDTO {
  /** "pending" | "reading" | "ok" | "failed". */
  status: string;
  row_count: number | string;
  fetched_at: string | null;
  error: string | null;
}

/** Every dataset the read-back can hand a review screen. */
export interface ZohoRecordsDTO {
  organisation?: ZohoOrganisationDTO | null;
  gstins?: ZohoGstinDTO[];
  ledgers?: ZohoLedgerDTO[];
  tds_rates?: ZohoTaxDTO[];
  tcs_rates?: ZohoTaxDTO[];
  tax_rates?: ZohoTaxDTO[];
  bank_accounts?: ZohoBankAccountDTO[];
  currencies?: ZohoCurrencyDTO[];
  /** How old each requested dataset is, keyed the same way. */
  readbacks?: Partial<Record<ZohoDatasetKey, ZohoDatasetReadDTO | null>>;
}

interface RecordsEnvelope {
  success: boolean;
  message: string;
  data: { records: ZohoRecordsDTO };
}

/**
 * What we already read out of the client's books. Pure read: it calls Zoho for
 * nothing and starts no clock, so a review screen can load it freely.
 *
 * `datasets` narrows it to what the screen draws. Ask for everything and you
 * get a hundred and twenty ledgers along with the currencies you wanted.
 */
export const fetchZohoRecords = async (
  publicId: string,
  datasets: ZohoDatasetKey[]
): Promise<ZohoRecordsDTO> =>
  (await postJson<RecordsEnvelope>("/api/onboarding/zoho/records", {
    public_id: publicId,
    datasets,
  })).data.records;

/**
 * Pull one dataset out of Zoho again and hand back the fresh rows.
 *
 * The one call in this pair that is not free — it costs a Zoho request and
 * takes as long as Zoho takes, so it belongs behind a button rather than in an
 * effect. Confirmations survive it: rows are updated in place, not replaced.
 *
 * A Zoho failure resolves rather than throwing. The reason arrives on
 * `readbacks[dataset]`, alongside whatever rows we still hold from last time.
 */
export const refreshZohoDataset = async (
  publicId: string,
  dataset: ZohoDatasetKey
): Promise<ZohoRecordsDTO> =>
  (await postJson<RecordsEnvelope>("/api/onboarding/zoho/records/refresh", {
    public_id: publicId,
    dataset,
  })).data.records;
